import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import {
  NotificationSource,
  UserNotificationItem,
  UserNotificationType,
} from 'src/app/Data/service/http-help.service';
import { NotificationLogState, PushDeviceService } from 'src/app/shared/services/push-device.service';

@Component({
  selector: 'app-push-device-bell',
  templateUrl: './push-device-bell.component.html',
  styleUrls: ['./push-device-bell.component.scss'],
})
export class PushDeviceBellComponent implements OnInit, OnDestroy {
  open = false;
  enabled = false;
  loading = true;
  unreadCount = 0;
  log: NotificationLogState = { items: [], total: 0, page: 1, loading: false, hasMore: false, error: null };

  private statusSub?: Subscription;
  private unreadSub?: Subscription;
  private logSub?: Subscription;

  constructor(
    private readonly pushDevice: PushDeviceService,
    private readonly router: Router,
    private readonly translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.statusSub = this.pushDevice.status$.subscribe((s) => {
      this.enabled = s?.notifications_enabled === true;
      this.loading = false;
    });
    this.unreadSub = this.pushDevice.unreadCount$.subscribe((c) => {
      this.unreadCount = c;
    });
    this.logSub = this.pushDevice.notifLog$.subscribe((l) => {
      this.log = l;
    });
  }

  ngOnDestroy(): void {
    this.statusSub?.unsubscribe();
    this.unreadSub?.unsubscribe();
    this.logSub?.unsubscribe();
  }

  toggleDropdown(): void {
    this.open = !this.open;
    if (this.open && (this.log.error || this.log.items.length === 0)) {
      void this.pushDevice.loadNotifications(true);
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.push-device-bell')) {
      this.open = false;
    }
  }

  loadMore(): void {
    const isError = this.log.error != null;
    void this.pushDevice.loadNotifications(isError);
  }

  markAllRead(): void {
    void this.pushDevice.markAllRead();
  }

  openCenter(event: MouseEvent): void {
    event.stopPropagation();
    this.open = false;
    void this.router.navigateByUrl('/dashboard/notifications');
  }

  openSettings(event: MouseEvent): void {
    event.stopPropagation();
    this.open = false;
    void this.router.navigateByUrl('/dashboard/notification-details');
  }

  onItemClick(event: MouseEvent, item: UserNotificationItem): void {
    event.stopPropagation();
    this.open = false;
    void this.pushDevice.openNotificationItem(item);
  }

  getTypeIcon(type: UserNotificationType | null | undefined): string {
    const icons: Record<string, string> = {
      new_task: 'clipboard',
      new_message: 'message-circle',
      file: 'paperclip',
      mention: 'at-sign',
      merchant_reply: 'corner-up-left',
      task_closed: 'check-circle',
      task_reopened: 'rotate-ccw',
      postponement_expired: 'clock',
      task_assigned: 'user-check',
      participant_added: 'users',
      broadcast: 'radio',
      expense_analysis_complete: 'file-text',
      subscription_renewal_failed: 'alert-circle',
      subscription_payment: 'credit-card',
      office_permission: 'shield',
      accountant_access_granted: 'unlock',
      closed_month: 'calendar',
      custom_payment: 'dollar-sign',
    };
    return icons[type || ''] || 'bell';
  }

  typeLabel(type: UserNotificationType | null | undefined): string {
    if (!type) {
      return '';
    }
    const key = `notifications.center.types.${type}`;
    const translated = this.translate.instant(key);
    return translated !== key ? translated : type;
  }

  sourceLabel(source: NotificationSource | null | undefined): string {
    if (!source) {
      return '';
    }
    const key = `notifications.center.sources.${source}`;
    const translated = this.translate.instant(key);
    return translated !== key ? translated : source;
  }

  trackById(_: number, item: UserNotificationItem): number {
    return item.id;
  }
}
